import { AuthUtils, CloudActions } from '@portal/portal-core';
import get from 'lodash-es/get';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import type { RootState } from 'reducers';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import {
  AgreementActions,
  AgreementClientConstants,
  AgreementClientSelectors,
} from 'ducks/agreements-client';
import { AuthClientUtils } from 'ducks/auth-client';
import { updateCustomFieldValuesWithInvite } from 'ducks/custom-fields/actions';
import { UPDATE_CUSTOM_FIELD_VALUES_WITH_INVITE } from 'ducks/custom-fields/types';
import { UserClientActions, UserClientSelectors } from 'ducks/user-client';

import Notifications from 'components/notifications';
import DownloadConfirmationNotification from 'components/notifications/download-confirmation';
import GlobalConfirmationNotification from 'components/notifications/global-confirmation';
import {
  currentTabSelector,
  getInviteAcceptance,
  selectIsAuthenticated,
  selectSuperAdminCostCenterVisit,
  selectUserCustomFieldFormInfo,
  selectUserCustomFieldValuesResult,
  selectUserId,
} from 'selectors';

import { Footer } from './footer';
import Header from './header';
import { LayoutContext } from './layout-context';
import Router from './router';

const { isClientConfigReady } = AuthClientUtils;

// Footer is authored as a plain render function (legacy `testHook` signature); expose it as a
// component so it can be used in JSX without the parameter being treated as a props type.
const FooterView = Footer as unknown as React.FC;

// These confirmation notifications are class components whose props are typed as
// `Record<string, never>`; that index signature rejects React's special `ref` attribute.
// Re-type them as ref-able components so we can attach an imperative ref.
const GlobalConfirmationView =
  GlobalConfirmationNotification as unknown as React.ComponentClass<Record<never, never>>;
const DownloadConfirmationView =
  DownloadConfirmationNotification as unknown as React.ComponentClass<Record<never, never>>;

// The Notifications component ignores `userId`/`productName` at runtime, but the portal has
// historically passed them; widen the accepted props here so we keep passing them unchanged.
const NotificationsView = Notifications as unknown as React.ComponentType<
  Omit<React.ComponentProps<typeof Notifications>, 'ref'> & {
    userId?: unknown;
    productName?: unknown;
  }
>;

// Props accepted by the Notifications component, used to coerce values coming from
// loosely-typed selectors/action creators at the call site.
type NotificationsProps = React.ComponentProps<typeof NotificationsView>;

// Exported for e2e/fixtures/china-footer.tsx, which mounts the same shell so the
// in-China footer's placement is asserted against the production container rather
// than a stand-in.
export const MainContainer = styled.div`
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const EMAIL_VERIFICATION_PENDING_STATES: string[] = [
  CloudActions.EMAIL_VERIFICATION_FETCHING,
  CloudActions.EMAIL_VERIFICATION_EXPIRED,
  CloudActions.EMAIL_VERIFICATION_FAILED,
  CloudActions.EMAIL_VERIFICATION_UNVERIFIED,
];

// Imperative handle exposed by the confirmation notification components (untyped .js).
interface ConfirmationHandle {
  confirm: (...args: unknown[]) => unknown;
}

// Minimal shape of an invite as consumed here (selector is from an untyped .js duck).
interface Invite {
  token: string;
}

export const Layout = (): React.JSX.Element => {
  const dispatch = useAppDispatch();

  const configIsReady = isClientConfigReady();
  const authenticated = useAppSelector(selectIsAuthenticated);
  const eulaAgreementUiState = useAppSelector((state: RootState) =>
    AgreementClientSelectors.getEulaAgreementUiState(get(state, 'agreements')),
  );
  const invites = useAppSelector(UserClientSelectors.selectInvites);
  const inviteAcceptanceState = useAppSelector(getInviteAcceptance);
  const superAdminCostCenter = useAppSelector(selectSuperAdminCostCenterVisit);
  const isVerifyingEmail = useAppSelector((state: RootState) =>
    EMAIL_VERIFICATION_PENDING_STATES.includes(get(state, 'emailVerification.uiState')),
  );
  const userId = useAppSelector(selectUserId);
  const customFieldsInfo = useAppSelector(selectUserCustomFieldFormInfo);
  const customFieldValuesResult = useAppSelector(selectUserCustomFieldValuesResult) as
    | { requestType?: string; success?: boolean; error?: unknown }
    | undefined;
  const customFieldsSubmitFailure =
    customFieldValuesResult?.requestType === UPDATE_CUSTOM_FIELD_VALUES_WITH_INVITE &&
    customFieldValuesResult.success === false
      ? customFieldValuesResult
      : undefined;
  const productName = useAppSelector(currentTabSelector);

  const acceptInvite = useCallback(
    (...args: Parameters<typeof UserClientActions.acceptInvite>) =>
      dispatch(UserClientActions.acceptInvite(...args)),
    [dispatch],
  );
  const dismissInviteConfirmation = useCallback(
    (...args: Parameters<typeof UserClientActions.clientAcceptInviteResultClear>) =>
      dispatch(UserClientActions.clientAcceptInviteResultClear(...args)),
    [dispatch],
  );
  const getUserAgreement = useCallback(
    (...args: Parameters<typeof AgreementActions.clientUserAgreementGet>) =>
      dispatch(AgreementActions.clientUserAgreementGet(...args)),
    [dispatch],
  );
  const updateCustomFieldsForUser = useCallback(
    (...args: Parameters<typeof updateCustomFieldValuesWithInvite>) =>
      dispatch(updateCustomFieldValuesWithInvite(...args)),
    [dispatch],
  );

  const [dismissedInviteTokens, setDismissedInviteTokens] = useState<string[]>([]);

  const globalConfirmationRef = useRef<ConfirmationHandle | null>(null);
  const globalDownloadConfirmationRef = useRef<ConfirmationHandle | null>(null);
  const contextValue = useMemo(
    () => ({
      confirm: (...args: unknown[]) =>
        globalConfirmationRef.current && globalConfirmationRef.current.confirm(...args),
      confirmDownload: (...args: unknown[]) =>
        globalDownloadConfirmationRef.current &&
        globalDownloadConfirmationRef.current.confirm(...args),
    }),
    [],
  );

  // componentDidUpdate: once the user becomes authenticated (auth flips to true),
  // fetch the user agreement. Skipped on mount to match the original behaviour.
  const prevAuthRef = useRef(authenticated);
  useEffect(() => {
    if (authenticated && authenticated !== prevAuthRef.current) {
      getUserAgreement(AuthUtils.getCombinedEulaFilterSettings());
    }
    prevAuthRef.current = authenticated;
  }, [authenticated, getUserAgreement]);

  const dismissInvite = useCallback((inviteToken: string) => {
    setDismissedInviteTokens((prev) => prev.concat([inviteToken]));
  }, []);

  const missingAgreements = !!(
    authenticated &&
    eulaAgreementUiState === AgreementClientConstants.eulaAgreementConstants.needsToAgree
  );

  const fetchingAgreements = !!(
    authenticated &&
    eulaAgreementUiState === AgreementClientConstants.eulaAgreementConstants.waitingForApiResponses
  );

  return (
    <LayoutContext.Provider value={contextValue}>
      <MainContainer>
        {!missingAgreements && (
          <React.Fragment>
            <GlobalConfirmationView
              ref={(node) => {
                // Adapt the class-component instance to our imperative handle.
                globalConfirmationRef.current = node as unknown as ConfirmationHandle | null;
              }}
            />
            <DownloadConfirmationView
              ref={(node) => {
                // Adapt the class-component instance to our imperative handle.
                globalDownloadConfirmationRef.current = node as unknown as ConfirmationHandle | null;
              }}
            />
            <NotificationsView
              superAdminCostCenter={
                superAdminCostCenter as NotificationsProps['superAdminCostCenter']
              }
              authenticated={authenticated}
              invites={
                invites.filter(
                  (invite: Invite) => !dismissedInviteTokens.includes(invite.token),
                ) as unknown as NotificationsProps['invites']
              }
              acceptInvite={acceptInvite}
              dismissInvite={dismissInvite}
              inviteAcceptanceState={
                inviteAcceptanceState as NotificationsProps['inviteAcceptanceState']
              }
              dismissInviteConfirmation={dismissInviteConfirmation}
              userId={userId}
              customFieldsFormInfo={customFieldsInfo}
              onSubmitCustomFields={
                updateCustomFieldsForUser as NotificationsProps['onSubmitCustomFields']
              }
              customFieldsSubmitFailure={customFieldsSubmitFailure}
              productName={productName}
            />
          </React.Fragment>
        )}
        <Header />
        <Router
          configIsReady={configIsReady}
          authenticated={authenticated}
          missingAgreements={missingAgreements}
          fetchingAgreements={fetchingAgreements}
          isVerifyingEmail={isVerifyingEmail}
        />
        <FooterView />
      </MainContainer>
    </LayoutContext.Provider>
  );
};

export default Layout;
